import { BrowserCache } from '@/utils/cache'
import { ModelDefaults } from './models'
import { ModelMessage } from '@/types/msq'
import {
  addAgent,
  agentCompletionRequest,
  deleteAgent,
  generatePromptRequest,
  getPublishedAgents,
  getSharedAgents,
  toggleAgentPublish,
  listPublicSessions as apiListPublicSessions,
  savePublicSession as apiSavePublicSession,
  updatePublicSessionTitle as apiUpdatePublicSessionTitle,
  deletePublicSession as apiDeletePublicSession,
  synthesizeSpeech,
  getUserSettings,
  updateUserSettings,
  getSharedAgentConfig as apiGetSharedAgentConfig,
  updateSharedAgentConfig as apiUpdateSharedAgentConfig,
  getSharedAgentOwnerTools as apiGetSharedAgentOwnerTools,
  getSharedAgentOwnerEmbeddings as apiGetSharedAgentOwnerEmbeddings,
  getSharedAgentOwnerVoices as apiGetSharedAgentOwnerVoices,
  SharedAgentUpdatePayload,
  SharedAgentOwnerToolsResponse,
  SharedAgentOwnerEmbeddingsResponse,
  SharedAgentOwnerVoicesResponse
} from '@/utils/api'
import { isApiError } from '@/types/api'
import { Commit, Dispatch } from 'vuex'
import { PromptMessage } from '@/types/general'
import { AgentConfig, AgentMap, UserAgentConfig, SharedAgentInfo } from '@/types/agents'
import { createHash } from '@/utils/general'
import { PublicChatSession } from '@/types/public'
import { ModelConfig } from '@/types/models'
import { compressData, decompressData } from '@/utils/compression'
import { CompressedPublicChatSession } from '@/types/compressed'
import { isChatEmpty } from '@/utils/messages'
import { formatOwnedAgentRef, formatSharedAgentRef } from '@/utils/agentRefs'

export interface AgentDefaults {
  options: ModelDefaults
  prompt: PromptMessage
}

export interface AgentsState {
  configs: Record<string, UserAgentConfig>
  defaults: AgentDefaults
  publicChatHistory: CompressedPublicChatSession[] // Changed to use compressed type
  sharedAgents: SharedAgentInfo[] // Agents shared with the current user
  utilityAgents: {
    titleAgent: string | null
    configAgent: string | null
  }
}

export interface AccessibleAgentItem {
  ref: string
  kind: 'owned' | 'shared'
  title: string
  subtitle: string
  agentId?: string
  ownerUsername?: string
  slug?: string
  sharedAgent?: SharedAgentInfo
  ownedConfig?: UserAgentConfig
}

const AGENT_DEFAULTS: AgentDefaults = {
  options: {
    temperature: 0.2
  },
  prompt: {
    role: 'user',
    content:
      'Create a prompt for an AI agent to the best of your ability. Only include the prompt, do not include any other language or explanation. Do not include a prefix or qualifier like "Prompt:" or "Agent Prompt". ONLY INCLUDE THE PROMPT, THIS IS CRITICAL. This is going to be auto-filled into the prompt for the agent and should not have to be modified by the user. You and the agent may not give instructions to the user or rely on the user to do anything at all. It is critical the the agent is told that it cannot reply with instructions for the user to complete the task and this must be stated in the prompt for the agent. If there are files to be created just provide the code and filename. This should be from the user\'s point of view, not yours and thus should include pronouns like "you" and "your". Do not use your prompt or any elements from your prompt. Do not use quotes around the pompt. Do not make it like this prompt. It needs to be unique and should apply to the description. It should be verbose enough so that the agent knows exactly what to do. If this agent needs to get clarity or communicate with another agent, that needs to be 100% clear in the request so that the agent manager knows which agent to route the response to. When done with the task, the agent should respond with "TERMINATE" so that the agent manager knows that the task is complete. The description for the prompt is in the 3rd person point of view. Here is the agent name and description to use for creating the prompt. name: <name>. description: <description>'
  }
}

const ENABLE_SERVER_PUBLIC_CHAT_READS = true
const ENABLE_SERVER_PUBLIC_CHAT_WRITES = false // Public chat persistence is backend-authoritative from the streaming API.

export interface CachedAgentsState {
  generatePrompt?: string
  utilityAgents?: {
    titleAgent?: string | null
    configAgent?: string | null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isUtilityAgentName(value: unknown): value is string | null {
  return typeof value === 'string' || value === null
}

function toCachedUtilityAgents(value: unknown): CachedAgentsState['utilityAgents'] | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const utilityAgents: NonNullable<CachedAgentsState['utilityAgents']> = {}
  if (isUtilityAgentName(value.titleAgent)) {
    utilityAgents.titleAgent = value.titleAgent
  }
  if (isUtilityAgentName(value.configAgent)) {
    utilityAgents.configAgent = value.configAgent
  }

  return utilityAgents.titleAgent === undefined && utilityAgents.configAgent === undefined
    ? undefined
    : utilityAgents
}

export function toCachedAgentsState(
  state: Partial<AgentsState> & Partial<CachedAgentsState>
): CachedAgentsState {
  const legacyGeneratePrompt = state.defaults?.prompt.content
  const generatePrompt =
    state.generatePrompt ?? (typeof legacyGeneratePrompt === 'string' ? legacyGeneratePrompt : undefined)
  const utilityAgents = toCachedUtilityAgents(state.utilityAgents)

  return {
    ...(generatePrompt === undefined ? {} : { generatePrompt }),
    ...(utilityAgents === undefined ? {} : { utilityAgents })
  }
}

function persistAgentPreferences(cache: BrowserCache, state: AgentsState): void {
  cache.add('agents', toCachedAgentsState(state))
}

export const agents = (cached: Partial<CachedAgentsState>, cache: BrowserCache) => ({
  state: <AgentsState>{
    configs: {},
    defaults: {
      ...AGENT_DEFAULTS,
      prompt: {
        ...AGENT_DEFAULTS.prompt,
        ...(cached.generatePrompt === undefined ? {} : { content: cached.generatePrompt })
      }
    },
    publicChatHistory: [],
    sharedAgents: [],
    utilityAgents: {
      titleAgent: cached.utilityAgents?.titleAgent ?? null,
      configAgent: cached.utilityAgents?.configAgent ?? null
    }
  },
  mutations: {
    setAgentConfig(state: AgentsState, { config, previousName }: { config: AgentConfig; previousName?: string }) {
      // Handle rename: remove old key if the name changed
      if (previousName && previousName !== config.name && state.configs[previousName]) {
        delete state.configs[previousName]
      }
      state.configs[config.name] = { type: 'user-agent', config }
    },
    deleteAgentConfig(state: AgentsState, name: string) {
      // Find the agent by name and delete it by name
      const agentEntry = state.configs[name]
      if (agentEntry) {
        delete state.configs[name]
      }
    },
    updateGeneratePrompt(state: AgentsState, prompt: string) {
      state.defaults.prompt.content = prompt
      persistAgentPreferences(cache, state)
    },
    setAgentConfigs(state: AgentsState, map: AgentMap) {
      state.configs = Object.entries(map).reduce((acc, [_, config]) => {
        const name = config.config.name
        // Preserve publishedInfo from existing state if it exists
        // (publishedInfo is managed separately via fetchPublishedAgents)
        const existingPublishedInfo = state.configs[name]?.config?.publishedInfo
        if (existingPublishedInfo) {
          config.config.publishedInfo = existingPublishedInfo
        }
        acc[name] = config
        return acc
      }, {} as Record<string, UserAgentConfig>)
    },
    setPublishedAgents(
      state: AgentsState,
      { publishedAgents, username }: { publishedAgents: any[]; username: string }
    ) {
      publishedAgents.forEach((p) => {
        if (state.configs[p.agentName]) {
          state.configs[p.agentName].config.publishedInfo = {
            ...p,
            publicUrl: `${window.location.origin}/agent/${username}/${p.slug}`
          }
        }
      })
    },
    setSharedAgents(state: AgentsState, sharedAgents: SharedAgentInfo[]) {
      state.sharedAgents = sharedAgents
    },
    setPublicChatHistory(state: AgentsState, history: PublicChatSession[]) {
      // Compress messages for each session
      state.publicChatHistory = history.map((session) => ({
        ...session,
        messages: compressData(session.messages)
      }))
    },
    mergePublicChatHistory(
      state: AgentsState,
      { agentUsername, agentSlug, history }: 
      { agentUsername: string; agentSlug: string; history: PublicChatSession[] }
    ) {
      // Remove existing sessions for this specific agent
      state.publicChatHistory = state.publicChatHistory.filter(
        (chat) => !(chat.agentUsername === agentUsername && chat.agentSlug === agentSlug)
      )
      
      // Add new sessions for this agent (compressed)
      const newSessions = history.map((session) => ({
        ...session,
        messages: compressData(session.messages)
      }))
      
      state.publicChatHistory.push(...newSessions)
      
      // Sort to keep most recently updated first
      state.publicChatHistory.sort((a, b) => b.updatedAt - a.updatedAt)
      
    },
    addOrUpdatePublicChat(state: AgentsState, chatSession: PublicChatSession) {
      // Compress messages before storing
      const compressedSession: CompressedPublicChatSession = {
        ...chatSession,
        messages: compressData(chatSession.messages)
      }

      const index = state.publicChatHistory.findIndex((chat) => chat.id === chatSession.id)
      if (index !== -1) {
        state.publicChatHistory[index] = compressedSession
      } else {
        state.publicChatHistory.push(compressedSession)
      }
      // Sort to keep the most recently updated chats first
      state.publicChatHistory.sort((a, b) => b.updatedAt - a.updatedAt)
      // De-duplicate by id to avoid transient duplicates (e.g., title updates racing with new chat creation)
      {
        const seen = new Set<string>()
        state.publicChatHistory = state.publicChatHistory.filter((s) => {
          if (seen.has(s.id)) return false
          seen.add(s.id)
          return true
        })
      }
    },
    deletePublicChat(state: AgentsState, chatId: string) {
      state.publicChatHistory = state.publicChatHistory.filter((chat) => chat.id !== chatId)
    },
    setTitleAgent(state: AgentsState, agentName: string | null) {
      state.utilityAgents.titleAgent = agentName
      persistAgentPreferences(cache, state)
    },
    setConfigAgent(state: AgentsState, agentName: string | null) {
      state.utilityAgents.configAgent = agentName
      persistAgentPreferences(cache, state)
    }
  },
  actions: {
    async fetchUserSettings({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      try {
        const res = await getUserSettings()
        if (!isApiError(res) && res.success && res.settings?.utilityAgents) {
          const t = res.settings.utilityAgents.titleAgent ?? null
          commit('setTitleAgent', t)
          const c = res.settings.utilityAgents.configAgent ?? null
          commit('setConfigAgent', c)
        }
      } catch (e) {
        dispatch('addLogEvent', { title: 'User Settings Fetch Error', content: String((e as Error).message || e) }, { root: true } as any)
      }
    },

    async saveTitleAgent({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, agentName: string | null) {
      // Optimistic local update to keep UX responsive
      commit('setTitleAgent', agentName)
      try {
        const res = await updateUserSettings({ utilityAgents: { titleAgent: agentName } })
        if (isApiError(res) || !res.success) {
          throw new Error('Failed to save user settings')
        }
      } catch (e: any) {
        dispatch('updateSnackbar', { text: 'Failed to save Summary Agent preference', color: 'error' }, { root: true } as any)
        dispatch('addLogEvent', { title: 'User Settings Save Error', content: String(e?.message || e) }, { root: true } as any)
      }
    },
    async fetchPublicChatHistoryForAgent(
      { commit }: { commit: Commit },
      { agentUsername, agentSlug }: { agentUsername: string; agentSlug: string }
    ) {
      if (!ENABLE_SERVER_PUBLIC_CHAT_READS) return
      const res = await apiListPublicSessions(agentUsername, agentSlug, 100, 0)
      if (!isApiError(res) && (res as any).success && Array.isArray((res as any).data)) {
        commit('mergePublicChatHistory', { 
          agentUsername, 
          agentSlug, 
          history: (res as any).data 
        })
      }
    },

    async toggleAgentPublish(
      { dispatch }: { dispatch: Dispatch },
      { agentId, agentName }: { agentId: string; agentName: string }
    ) {
      try {
        const response = await toggleAgentPublish(agentId, agentName)
        if (!isApiError(response)) {
          await dispatch('fetchPublishedAgents') // Refresh the list
          dispatch('updateSnackbar', { text: `Agent ${agentName} publish status updated.` }, { root: true })
        } else {
          // Revert the UI state by fetching the current state from the server
          await dispatch('fetchPublishedAgents')
          dispatch(
            'updateSnackbar',
            { text: `Agent Publish Error: ${response.message}`, color: 'error' },
            { root: true }
          )
          dispatch('addLogEvent', { title: 'Agent Publish Error', content: response }, { root: true })
        }
      } catch (error) {
        // Revert the UI state on any unexpected error
        await dispatch('fetchPublishedAgents')
        dispatch(
          'updateSnackbar',
          { text: 'An unexpected error occurred. Please try again.', color: 'error' },
          { root: true }
        )
      }
    },

    async fetchPublishedAgents({ commit, rootGetters }: { commit: Commit; rootGetters: any }) {
      const response = await getPublishedAgents()
      if (!isApiError(response) && response.success) {
        const username = rootGetters.username
        if (username) {
          commit('setPublishedAgents', { publishedAgents: response.data, username })
        }
      }
    },

    async fetchSharedAgents({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      try {
        const response = await getSharedAgents()
        if (!isApiError(response) && response.success) {
          commit('setSharedAgents', response.data)
        } else if (isApiError(response)) {
          // If endpoint doesn't exist yet (404), silently fail
          if (response.statusCode !== 404) {
            console.warn('Failed to fetch shared agents:', response.message)
          }
        }
      } catch (error) {
        // Silently fail if endpoint doesn't exist yet
        console.warn('Shared agents endpoint not available:', error)
      }
    },

    async fetchSharedAgentConfig(
      { dispatch }: { dispatch: Dispatch },
      { ownerUsername, slug }: { ownerUsername: string; slug: string }
    ): Promise<{ config: AgentConfig; modelName: string } | null> {
      const res = await apiGetSharedAgentConfig(ownerUsername, slug)
      if (isApiError(res) || !res.success) {
        const message = isApiError(res) ? res.message : 'Failed to load shared agent'
        dispatch('updateSnackbar', { text: `Shared Agent Load Error: ${message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Shared Agent Load Error', content: res }, { root: true })
        return null
      }
      return { config: res.data, modelName: res.modelName ?? '' }
    },

    async fetchSharedAgentOwnerCatalogs(
      { dispatch }: { dispatch: Dispatch },
      { ownerUsername, slug }: { ownerUsername: string; slug: string }
    ): Promise<{
      tools: SharedAgentOwnerToolsResponse | null
      embeddings: SharedAgentOwnerEmbeddingsResponse | null
      voices: SharedAgentOwnerVoicesResponse | null
    }> {
      const [t, e, v] = await Promise.all([
        apiGetSharedAgentOwnerTools(ownerUsername, slug),
        apiGetSharedAgentOwnerEmbeddings(ownerUsername, slug),
        apiGetSharedAgentOwnerVoices(ownerUsername, slug)
      ])
      const tools = !isApiError(t) && t.success ? t : null
      const embeddings = !isApiError(e) && e.success ? e : null
      const voices = !isApiError(v) && v.success ? v : null
      if (tools == null || embeddings == null || voices == null) {
        const failed: string[] = []
        if (tools == null) failed.push('tools')
        if (embeddings == null) failed.push('embeddings')
        if (voices == null) failed.push('voices')
        dispatch(
          'updateSnackbar',
          { text: `Shared Agent Catalog Error: failed to load ${failed.join(', ')}`, color: 'error' },
          { root: true }
        )
      }
      return { tools, embeddings, voices }
    },

    async updateSharedAgentConfig(
      { dispatch }: { dispatch: Dispatch },
      {
        ownerUsername,
        slug,
        payload,
        agentName
      }: {
        ownerUsername: string
        slug: string
        payload: SharedAgentUpdatePayload
        agentName: string
      }
    ): Promise<boolean> {
      const res = await apiUpdateSharedAgentConfig(ownerUsername, slug, payload)
      if (isApiError(res) || res.status !== 'success') {
        const message = isApiError(res) ? res.message : res.message
        dispatch('updateSnackbar', { text: `Shared Agent Save Error: ${message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Shared Agent Save Error', content: res }, { root: true })
        return false
      }
      await dispatch('fetchSharedAgents')
      dispatch('updateSnackbar', { text: `Saved shared agent ${agentName}`, timeout: 3000 }, { root: true })
      dispatch(
        'addLogEvent',
        { title: 'Shared Agent Edited', content: { ownerUsername, slug, agentName } },
        { root: true }
      )
      return true
    },

    async setAgentConfig(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      payload: AgentConfig | { config: AgentConfig; previousName?: string }
    ) {
      // Support both legacy callers (plain AgentConfig) and rename-aware callers ({ config, previousName })
      const config: AgentConfig = 'config' in payload && 'name' in (payload as any).config ? (payload as { config: AgentConfig; previousName?: string }).config : payload as AgentConfig
      const previousName: string | undefined = 'previousName' in payload ? (payload as { config: AgentConfig; previousName?: string }).previousName : undefined

      const response = await addAgent(config, previousName)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Agent Config Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Agent Config Error', content: response }, { root: true })
        return false
      }

      // Use the server-assigned ID so publish/delete work immediately
      if (response.data?.id) {
        config.id = response.data.id
      }

      // Handle rename: delete old agent from server if name changed
      const isRename = previousName && previousName !== config.name
      if (isRename) {
        const deleteResponse = await deleteAgent(previousName)
        if (isApiError(deleteResponse)) {
          dispatch('addLogEvent', { title: 'Agent Rename Cleanup Warning', content: `Failed to delete old agent "${previousName}": ${deleteResponse.message}` }, { root: true })
        }
      }

      commit('setAgentConfig', { config, previousName })
      // Refresh published status (backend may auto-publish certain agents)
      dispatch('fetchPublishedAgents')
      dispatch('markConfigResourcesDirty', ['agents'], { root: true })
      const { systemPrompt, ...remainingConfig } = config
      const eventConfig = { ...remainingConfig, systemPromptHash: await createHash(systemPrompt) }
      dispatch('addLogEvent', { title: 'Agent Configuration Set', content: eventConfig }, { root: true })
      return true
    },
    async deleteAgentConfig(
      { commit, state, dispatch }: { commit: Commit; state: AgentsState; dispatch: Dispatch },
      name: string
    ) {
      // Find the agent by name
      const agentEntry = state.configs[name]
      if (agentEntry) {
        // Call the API to delete the agent on the server
        const response = await deleteAgent(name)

        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Agent Delete Error: ${response.message}` }, { root: true })
          dispatch('addLogEvent', { title: 'Agent Delete Error', content: response }, { root: true })
          return false
        }

        const { status, message } = response
        if (status === 'success') {
          // Only update local state if API call succeeds
          commit('deleteAgentConfig', name)
          dispatch('updateSnackbar', { text: message }, { root: true })
          dispatch('addLogEvent', { title: 'Agent Deleted', content: name }, { root: true })
          dispatch('markConfigResourcesDirty', ['agents'], { root: true })
          return true
        } else {
          dispatch('updateSnackbar', { text: `${status}: ${message}` }, { root: true })
          return false
        }
      } else {
        console.error('Agent not found with ID:', name)
        return false
      }
    },
    async generatePrompt(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { prompt, modelConfig }: { prompt: string; modelConfig: ModelConfig }
    ) {
      const response = await generatePromptRequest({ prompt, modelConfig })

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Prompt Generation Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Prompt Generation Error', content: response }, { root: true })
        return 'Error generating prompt'
      }

      dispatch(
        'addLogEvent',
        {
          title: 'Prompt Generated',
          content: {
            promptHash: await createHash(prompt),
            modelName: modelConfig.name
          }
        },
        { root: true }
      )
      return response.data
    },
    updateGeneratePrompt({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, { prompt }: { prompt: string }) {
      commit('updateGeneratePrompt', prompt)
      dispatch('addLogEvent', { title: 'Generate Prompt Template Updated', content: prompt }, { root: true })
    },
    async promptAgent(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      {
        messages,
        name,
        timeout,
        requestTimeoutMs,
        maxRetries
      }: {
        messages: ModelMessage[]
        name: string
        timeout?: number
        requestTimeoutMs?: number
        maxRetries?: number
      }
    ) {
      const response = await agentCompletionRequest({ messages, name, timeout, requestTimeoutMs, maxRetries })

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Agent Communication Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Agent Communication Error', content: response }, { root: true })
        return { error: true, message: 'Error communicating with agent' }
      }

      dispatch(
        'addLogEvent',
        {
          title: 'Agent Prompt Sent',
          content: {
            agentName: name,
            messageCount: messages.length
          }
        },
        { root: true }
      )
      return response
    },
    // Synthesize speech via API, validating payload and routing errors through store
    async synthesizeSpeechForText(
      { dispatch, rootState, rootGetters }: { dispatch: Dispatch; rootState: any; rootGetters: any },
      data: {
        text: string
        voiceName?: string
        providerKey?: string
        voice?: string
        model?: string
        responseFormat?: 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm'
        speed?: number
        ttsOptions?: Record<string, unknown>
        apiKey?: string
        url?: string
      }
    ): Promise<{ buffer: ArrayBuffer; contentType?: string } | { error: true; message: string }> {
      try {
        // Basic validation per API contract in missionsquad-api/src/controllers/core.ts
        if (!data || typeof data.text !== 'string' || data.text.trim() === '') {
          const err = { error: true as const, message: 'Invalid request: text is required.' }
          dispatch('updateSnackbar', { text: err.message, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'TTS Error', content: err }, { root: true })
          return err
        }

        // Resolve a default saved voice alias from store if none provided.
        // This allows public pages to still hit the TTS endpoint deterministically when the viewer has saved voices.
        let chosenVoiceName = (data.voiceName || '').trim()
        if (!chosenVoiceName) {
          // Ensure voices are loaded
          const hasVoices =
            rootState?.voices &&
            rootState.voices.configs &&
            Object.keys(rootState.voices.configs).length > 0

          if (!hasVoices) {
            try { await dispatch('fetchVoices', null, { root: true } as any) } catch { /* ignore */ }
          }

          const voiceConfigs = rootState?.voices?.configs || {}
          const firstVoiceKey = Object.keys(voiceConfigs)[0]
          if (firstVoiceKey) {
            chosenVoiceName = firstVoiceKey
          }
        }

        const hasRawVoice = typeof data.providerKey === 'string' && typeof data.voice === 'string'
        const payload = {
          text: data.text,
          // Prefer saved voice alias if available; otherwise allow raw provider+voice if explicitly provided by caller
          ...(chosenVoiceName ? { voiceName: chosenVoiceName } : {}),
          ...(chosenVoiceName ? {} : hasRawVoice ? { providerKey: data.providerKey, voice: data.voice } : {}),
          ...(data.model ? { model: data.model } : {}),
          ...(data.responseFormat ? { responseFormat: data.responseFormat } : { responseFormat: 'opus' as const }),
          ...(typeof data.speed === 'number' ? { speed: data.speed } : {}),
          ...(data.ttsOptions ? { ttsOptions: data.ttsOptions } : {}),
          ...(data.apiKey ? { apiKey: data.apiKey } : {}),
          ...(data.url ? { url: data.url } : {})
        }

        // If neither a saved voice alias nor raw provider+voice could be established, fail fast.
        if (!payload.voiceName && !(payload.providerKey && payload.voice)) {
          const err = { error: true as const, message: 'Invalid request: providerKey and voice are required when voiceName is not provided.' }
          dispatch('updateSnackbar', { text: err.message, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'TTS Error', content: err }, { root: true })
          return err
        }

        const resp = await synthesizeSpeech(payload)
        if ((resp as any)?.error) {
          const apiErr = resp as { error: true; message: string }
          dispatch('updateSnackbar', { text: `TTS Error: ${apiErr.message}`, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'TTS Error', content: apiErr }, { root: true })
          return apiErr
        }
        // Success path
        dispatch(
          'addLogEvent',
          { title: 'TTS Success', content: { responseFormat: payload.responseFormat || 'opus', voiceName: payload.voiceName } },
          { root: true }
        )
        return resp as { buffer: ArrayBuffer; contentType?: string }
      } catch (e: any) {
        const err = { error: true as const, message: e?.message || 'TTS request failed' }
        dispatch('updateSnackbar', { text: `TTS Error: ${err.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'TTS Error', content: err }, { root: true })
        return err
      }
    },

    async savePublicChat({ commit }: { commit: Commit }, chatSession: PublicChatSession) {
      if (isChatEmpty(chatSession.messages)) {
        return
      }
      // Always update local cache first for responsiveness
      commit('addOrUpdatePublicChat', chatSession)

      if (ENABLE_SERVER_PUBLIC_CHAT_WRITES) {
        try {
          // Deep-clone the payload to avoid subsequent in-place mutations during streaming/title updates
          const payload: PublicChatSession = structuredClone(chatSession)
          await apiSavePublicSession(payload.agentUsername, payload.agentSlug, payload)
        } catch (e) {
          // best effort only; keep local cache
          console.warn('savePublicChat server sync failed', e)
        }
      }
    },
    async updatePublicChatTitle(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      chatSession: PublicChatSession
    ) {
      if (!chatSession.title.trim()) {
        return false
      }

      commit('addOrUpdatePublicChat', chatSession)

      try {
        const response = await apiUpdatePublicSessionTitle(
          chatSession.agentUsername,
          chatSession.agentSlug,
          chatSession.id,
          chatSession.title,
          chatSession.updatedAt
        )

        if (isApiError(response) || !response.success) {
          throw new Error(isApiError(response) ? response.message : 'Failed to update chat title')
        }

        return true
      } catch (e: any) {
        dispatch(
          'updateSnackbar',
          { text: `Chat Title Error: ${e?.message || 'Failed to update chat title'}`, color: 'error' },
          { root: true } as any
        )
        dispatch(
          'addLogEvent',
          { title: 'Chat Title Update Error', content: String(e?.message || e) },
          { root: true } as any
        )
        return false
      }
    },
    setTitleAgent({ commit }: { commit: Commit }, agentName: string | null) {
      commit('setTitleAgent', agentName)
    },
    async removePublicChat({ commit, state }: { commit: Commit; state: AgentsState }, chatId: string) {
      // Capture the session before optimistic local deletion so we have route params for API call
      const sess = state.publicChatHistory.find((s) => s.id === chatId)

      // Optimistic local delete
      commit('deletePublicChat', chatId)

      if (ENABLE_SERVER_PUBLIC_CHAT_READS && sess) {
        try {
          await apiDeletePublicSession(sess.agentUsername, sess.agentSlug, chatId)
        } catch (e) {
          console.warn('removePublicChat server sync failed', e)
        }
      }
    }
  },
  getters: {
    agentNames: (state: AgentsState) => Object.keys(state.configs),
    agentConfigs: (state: AgentsState) => state.configs,
    agentConfigsList: (state: AgentsState) => Object.values(state.configs),
    accessibleAgentItems: (state: AgentsState): AccessibleAgentItem[] => {
      const ownedItems = Object.values(state.configs).map((ownedConfig) => ({
        ref: formatOwnedAgentRef(ownedConfig.config.id),
        kind: 'owned' as const,
        title: ownedConfig.config.name,
        subtitle: 'Owned by you',
        agentId: ownedConfig.config.id,
        ownedConfig
      }))
      const sharedItems = state.sharedAgents.map((sharedAgent) => ({
        ref: formatSharedAgentRef(sharedAgent.ownerUsername, sharedAgent.slug),
        kind: 'shared' as const,
        title: sharedAgent.agentName,
        subtitle: `Shared by @${sharedAgent.ownerUsername}`,
        agentId: sharedAgent.agentId,
        ownerUsername: sharedAgent.ownerUsername,
        slug: sharedAgent.slug,
        sharedAgent
      }))
      return [...ownedItems, ...sharedItems].sort((a, b) => {
        const byTitle = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
        if (byTitle !== 0) return byTitle
        const aOwner = a.kind === 'shared' ? a.ownerUsername : ''
        const bOwner = b.kind === 'shared' ? b.ownerUsername : ''
        return aOwner.localeCompare(bOwner, undefined, { sensitivity: 'base' })
      })
    },
    agentDefaults: (state: AgentsState) => state.defaults,
    sharedAgents: (state: AgentsState) => state.sharedAgents,
    titleAgent: (state: AgentsState) => state.utilityAgents.titleAgent,
    configAgent: (state: AgentsState) => state.utilityAgents.configAgent,
    // Published slug of the config agent, so it can be selected in the Chat tab dropdown.
    // null if the config agent isn't provisioned/published.
    configAgentSlug: (state: AgentsState): string | null => {
      const name = state.utilityAgents.configAgent
      if (!name) return null
      const cfg = state.configs[name]
      return cfg?.config.publishedInfo?.slug ?? null
    },
    publicChatHistoryByAgent:
      (state: AgentsState) =>
      (agentUsername: string, agentSlug: string): PublicChatSession[] => {
        // Getter should only decompress, not migrate.
        return state.publicChatHistory
          .filter((chat) => chat.agentUsername === agentUsername && chat.agentSlug === agentSlug)
          .map((chat) => ({
            ...chat,
            messages: decompressData(chat.messages)
          }))
          .sort((a, b) => b.updatedAt - a.updatedAt)
      }
  }
})
